// Weekly slot calendar for one stylist. "Any stylist" times are not slots: they come from the stylists' free slots.
// Same flow as the reference product's "Assigned Slots" view: generate a weekly pattern
// (start, end, slot length, break, weekdays), browse week by week, customise one weekday,
// mark a full day or part of a day off, and remove single free slots. Booked slots never change.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api, { addDays, formatDate, isoDate } from '../services/api';
import { BackIcon, BusyOverlay, Confirm, Modal, Spinner } from '../components/ui';

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DEFAULT_GEN = { start_time: '10:00', end_time: '19:00', slot_duration: '30', break_start: '13:00', break_end: '14:00' };
const GEN_FIELDS = [
  ['start_time', 'Start Time', 'time'],
  ['end_time', 'End Time', 'time'],
  ['slot_duration', 'Duration (min)', 'number'],
  ['break_start', 'Break Start', 'time'],
  ['break_end', 'Break End', 'time'],
];
const PRESETS = [
  ['Mon–Fri', [0, 1, 2, 3, 4]],
  ['Mon–Sat', [0, 1, 2, 3, 4, 5]],
  ['All days', [0, 1, 2, 3, 4, 5, 6]],
  ['Sun', [6]],
];

function mondayOf(iso) {
  const d = new Date(`${iso}T00:00:00`);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - (day - 1));
  return isoDate(d);
}

function weekdayIndex(iso) {
  const d = new Date(`${iso}T00:00:00`).getDay();
  return d === 0 ? 6 : d - 1;
}

function toMinutes(t) {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + m;
}

/** Read start, end, length and break back from one weekday's slots. */
function inferGen(slots, dayIdx, fallback) {
  const day = slots.filter((s) => weekdayIndex(s.slot_date) === dayIdx).sort((a, b) => a.start_time.localeCompare(b.start_time));
  if (!day.length) return { ...fallback };
  // Use the fullest date, preferring one with no bookings (a booked slot can keep an older length).
  const dates = [...new Set(day.map((s) => s.slot_date))];
  const score = (d) => {
    const rows = day.filter((s) => s.slot_date === d);
    return rows.length - (rows.some((s) => s.booked) ? 1000 : 0);
  };
  const bestDate = dates.sort((a, b) => score(b) - score(a) || a.localeCompare(b))[0];
  const one = day.filter((s) => s.slot_date === bestDate);
  const first = one[0];
  const last = one[one.length - 1];
  const duration = Math.max(5, toMinutes(first.end_time) - toMinutes(first.start_time));
  let breakStart = '';
  let breakEnd = '';
  for (let i = 0; i < one.length - 1; i += 1) {
    if (one[i].end_time < one[i + 1].start_time) {
      breakStart = one[i].end_time;
      breakEnd = one[i + 1].start_time;
      break;
    }
  }
  return { start_time: first.start_time, end_time: last.end_time, slot_duration: String(duration), break_start: breakStart, break_end: breakEnd };
}

function genPayload(ownerId, weekdays, gen) {
  const payload = {
    staff_id: ownerId,
    weekdays,
    start_time: gen.start_time,
    end_time: gen.end_time,
    slot_duration: Number(gen.slot_duration) || 30,
  };
  if (gen.break_start && gen.break_end) {
    payload.break_start = gen.break_start;
    payload.break_end = gen.break_end;
  }
  return payload;
}

function generatedMessage(res, name) {
  if (!res) return `Schedule updated for ${name}`;
  return `Slots generated for ${name} (${res.dates_generated} days, ${res.slots_created} slots until ${formatDate(res.generated_until)})`;
}

// ── modals ───────────────────────────────────────────────────────────────────

function GenFields({ gen, setGen, compact }) {
  return (
    <div className={`nye-form-grid nye-form-grid--5`} style={compact ? { gap: 12 } : undefined}>
      {GEN_FIELDS.map(([key, label, type]) => (
        <div key={key} className="nye-field">
          <span className="nye-label">{label}</span>
          <input
            className="nye-input nye-input--bordered"
            type={type}
            min={type === 'number' ? 5 : undefined}
            max={type === 'number' ? 480 : undefined}
            step={type === 'number' ? 5 : undefined}
            value={gen[key] ?? ''}
            onChange={(e) => setGen((p) => ({ ...p, [key]: e.target.value }))}
          />
        </div>
      ))}
    </div>
  );
}

function CustomizeDayModal({ day, ownerName, onApply, onRemoveAll, onClose }) {
  const [gen, setGen] = useState({ ...day.gen });
  const [confirmRemove, setConfirmRemove] = useState(false);
  return (
    <Modal title={`Customise ${day.label} slots`} onClose={onClose} size="wide">
      <div className="nye-hint" style={{ marginBottom: 18 }}>
        Adjust times for <strong>{day.label}</strong>. This updates every <strong>{day.label}</strong> from today through the next year.
        Booked slots and time already marked off are kept.
      </div>
      <GenFields gen={gen} setGen={setGen} compact />
      <div className="nye-row nye-row--between nye-row--wrap" style={{ marginTop: 24 }}>
        <button type="button" className="nye-btn nye-btn--danger nye-btn--sm" onClick={() => setConfirmRemove(true)}>
          Remove all {day.label} slots
        </button>
        <div className="nye-row">
          <button type="button" className="nye-btn nye-btn--outline" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="nye-btn nye-btn--primary" onClick={() => onApply(day.dayIndex, gen)}>
            Apply to all {day.label}s
          </button>
        </div>
      </div>
      {confirmRemove ? (
        <Confirm
          title="Remove slots?"
          message={`Remove all future free ${day.label} slots for ${ownerName}? Booked slots stay.`}
          confirmLabel="Remove"
          onCancel={() => setConfirmRemove(false)}
          onConfirm={() => {
            setConfirmRemove(false);
            onRemoveAll(day.dayIndex);
          }}
        />
      ) : null}
    </Modal>
  );
}

function LeaveTimeModal({ ownerName, date, onSave, onClose }) {
  const [start, setStart] = useState('13:00');
  const [end, setEnd] = useState('16:00');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (start >= end) {
      toast.error('End time must be after start');
      return;
    }
    setSaving(true);
    try {
      await onSave(start, end);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title="Leave for part of the day" onClose={onClose}>
      <div className="nye-hint" style={{ marginBottom: 14 }}>
        Mark <strong>{ownerName}</strong> off on <strong>{formatDate(date)}</strong> between these times. Free slots in this window stop
        being offered on WhatsApp and voice calls. Booked slots stay as they are.
      </div>
      <div className="nye-form-grid nye-form-grid--2">
        <div className="nye-field">
          <span className="nye-label">From</span>
          <input className="nye-input nye-input--bordered" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="nye-field">
          <span className="nye-label">To</span>
          <input className="nye-input nye-input--bordered" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>
      <div className="nye-form-actions">
        <button type="button" className="nye-btn nye-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="nye-btn nye-btn--primary" onClick={save} disabled={saving}>
          {saving ? <Spinner light /> : null}
          Mark leave time
        </button>
      </div>
    </Modal>
  );
}

// ── main view ────────────────────────────────────────────────────────────────

export default function SlotCalendar({ owner, showGenerateForm = true, onBack }) {
  const ownerId = owner.id;
  const today = isoDate();

  const [weekStart, setWeekStart] = useState(() => mondayOf(today));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [gen, setGen] = useState(DEFAULT_GEN);
  const [selDays, setSelDays] = useState([]);
  const [savedDays, setSavedDays] = useState([]);
  const [customizeMode, setCustomizeMode] = useState(false);
  const [customizeDay, setCustomizeDay] = useState(null);
  const [leaveTimeDate, setLeaveTimeDate] = useState(null);
  const [prefilled, setPrefilled] = useState(false);

  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const ownerParams = { staff_id: ownerId };

  const loadWeek = async (syncSelection = false) => {
    setLoading(true);
    try {
      const weekRows = await api.listSlots({ ...ownerParams, date_from: weekDates[0], date_to: weekDates[6], include_past: 'true' });
      setSlots(weekRows);
      if (syncSelection) {
        // Which weekdays are in the schedule: look at the coming 7 days, not the visible week.
        const ahead = await api.listSlots({ ...ownerParams, date_from: today, date_to: addDays(today, 6) });
        // A weekday whose only slots are booked leftovers is not part of the schedule any more.
        const scheduled = ahead.filter((s) => !s.booked);
        const days = [...new Set(scheduled.map((s) => weekdayIndex(s.slot_date)))].sort((a, b) => a - b);
        setSavedDays(days);
        setSelDays(days);
        if (!prefilled && days.length) {
          const fullest = days.reduce((best, d) => {
            const n = scheduled.filter((s) => weekdayIndex(s.slot_date) === d).length;
            return n > best.n ? { d, n } : best;
          }, { d: days[0], n: -1 }).d;
          setGen(inferGen(ahead, fullest, DEFAULT_GEN));
          setPrefilled(true);
        }
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load slots');
    } finally {
      setLoading(false);
    }
  };

  // Re-read the weekday selection only when the owner changes or the schedule itself changes,
  // so moving between weeks or marking a slot off keeps an unsaved selection.
  const syncedOwner = useRef(undefined);
  useEffect(() => {
    const sync = syncedOwner.current !== ownerId;
    syncedOwner.current = ownerId;
    loadWeek(sync);
  }, [ownerId, weekStart]);

  const run = async (fn, okMessage, syncSelection = false) => {
    setGenerating(true);
    try {
      const res = await fn();
      if (okMessage) toast.success(typeof okMessage === 'function' ? okMessage(res) : okMessage);
      await loadWeek(syncSelection);
      return res;
    } catch (e) {
      toast.error(e.message || 'Something went wrong');
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const toggleDay = (i) => setSelDays((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i].sort((a, b) => a - b)));

  const applySchedule = () => {
    const toRemove = savedDays.filter((d) => !selDays.includes(d));
    if (!selDays.length && !toRemove.length) {
      toast.error('Select at least one weekday');
      return;
    }
    if (selDays.length && (!gen.start_time || !gen.end_time || gen.end_time <= gen.start_time)) {
      toast.error('End time must be after start time');
      return;
    }
    run(
      async () => {
        if (toRemove.length) await api.clearWeekdays(ownerId, toRemove);
        return selDays.length ? api.generateSlots(genPayload(ownerId, selDays, gen)) : null;
      },
      (res) => (res ? generatedMessage(res, owner.name) : 'Selected days removed'),
      true,
    );
  };

  const applyCustomize = (dayIndex, dayGen) => {
    setCustomizeDay(null);
    setCustomizeMode(false);
    run(
      () => api.generateSlots(genPayload(ownerId, [dayIndex], dayGen)),
      (res) => generatedMessage(res, `${WEEK_DAYS[dayIndex]} — ${owner.name}`),
      true,
    );
  };

  const removeCustomize = (dayIndex) => {
    setCustomizeDay(null);
    setCustomizeMode(false);
    run(() => api.clearWeekdays(ownerId, [dayIndex]), (res) => `${WEEK_DAYS[dayIndex]} slots removed (${res?.removed ?? 0})`, true);
  };

  const setDayOff = (date, blocked) =>
    run(
      () => api.blockRange({ staff_id: ownerId, slot_date: date, blocked }),
      (res) => {
        if (!blocked) return 'Leave removed';
        return res?.booked_kept ? `Day marked off. ${res.booked_kept} booked slot${res.booked_kept > 1 ? 's' : ''} kept.` : 'Full day marked off';
      },
    );

  const saveLeaveTime = async (start, end) => {
    const res = await run(
      () => api.blockRange({ staff_id: ownerId, slot_date: leaveTimeDate, start_time: start, end_time: end, blocked: true }),
      (r) => (r?.booked_kept ? `Leave ${start}–${end} marked. ${r.booked_kept} booked slot${r.booked_kept > 1 ? 's' : ''} kept.` : `Leave ${start}–${end} marked`),
    );
    if (res) setLeaveTimeDate(null);
  };

  const slotAction = (slot, action) => {
    const calls = {
      block: () => api.blockSlot(slot.id),
      unblock: () => api.unblockSlot(slot.id),
      remove: () => api.deleteSlot(slot.id),
    };
    const messages = { block: 'This time marked off', unblock: 'Slot leave removed', remove: 'Slot removed' };
    run(calls[action], messages[action]);
  };

  const openCustomize = (dayIndex) =>
    setCustomizeDay({ dayIndex, label: WEEK_DAYS[dayIndex], gen: inferGen(slots, dayIndex, gen) });

  const slotsFor = (date) => slots.filter((s) => s.slot_date === date).sort((a, b) => a.start_time.localeCompare(b.start_time));
  const busy = loading || generating;

  return (
    <div className="nye-panel nye-fade">
      <div className="nye-view-head">
        <button type="button" className="nye-back-btn" onClick={onBack} aria-label="Back">
          <BackIcon />
        </button>
        <div className="nye-view-title">{`Assigned Slots - ${owner.name}`}</div>
      </div>

      {showGenerateForm ? (
        <div className="nye-gen-box">
          <GenFields gen={gen} setGen={setGen} />
          <div className="nye-hint" style={{ margin: '18px 0 12px' }}>
            Select weekdays, then click <strong>Apply schedule</strong> to generate or remove slots from today through the next year.
          </div>
          <div className="nye-row nye-row--wrap" style={{ marginBottom: 14 }}>
            {WEEK_DAYS.map((d, i) => (
              <button key={d} type="button" className={`nye-day-chip${selDays.includes(i) ? ' active' : ''}`} onClick={() => toggleDay(i)} disabled={generating}>
                {d}
              </button>
            ))}
          </div>
          <div className="nye-row nye-row--wrap" style={{ gap: 10 }}>
            {PRESETS.map(([label, days]) => (
              <button key={label} type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setSelDays([...days])} disabled={generating}>
                {label}
              </button>
            ))}
            <button type="button" className="nye-btn nye-btn--primary" onClick={applySchedule} disabled={generating} style={{ marginLeft: 4 }}>
              {generating ? <Spinner light /> : null}
              {generating ? 'Applying…' : 'Apply schedule'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="nye-row nye-row--wrap" style={{ marginBottom: 14, gap: 10 }}>
        <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setWeekStart(addDays(weekStart, -7))}>
          ← Prev
        </button>
        <span className="nye-small nye-muted">Week of {formatDate(weekStart)}</span>
        <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setWeekStart(addDays(weekStart, 7))}>
          Next →
        </button>
        {weekStart !== mondayOf(today) ? (
          <button type="button" className="nye-btn nye-btn--ghost nye-btn--sm" onClick={() => setWeekStart(mondayOf(today))}>
            This week
          </button>
        ) : null}
        <div className="nye-spacer" />
        <button
          type="button"
          className="nye-btn nye-btn--gold nye-btn--sm"
          disabled={generating}
          onClick={() => setCustomizeMode(true)}
          style={customizeMode ? { boxShadow: '0 0 0 2px rgba(29, 124, 119,0.35)' } : undefined}
        >
          {customizeMode ? '✓ Customise per day — pick a day below' : '⚙ Customise slot for each day'}
        </button>
        {customizeMode ? (
          <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setCustomizeMode(false)}>
            Cancel
          </button>
        ) : null}
      </div>
      {customizeMode ? (
        <div className="nye-hint" style={{ margin: '-6px 0 12px' }}>
          Click any day header to change the start and end time, slot length, or break for that weekday.
        </div>
      ) : null}

      <BusyOverlay busy={busy}>
        <div style={{ overflowX: 'auto' }}>
          <div className="nye-week-grid">
            {weekDates.map((date, di) => {
              const daySlots = slotsFor(date);
              const isPast = date < today;
              const free = daySlots.filter((s) => !s.booked);
              const blockedCount = free.filter((s) => s.blocked).length;
              const onLeave = free.length > 0 && blockedCount === free.length;
              const partial = blockedCount > 0 && !onLeave;
              return (
                <div
                  key={date}
                  className={`nye-day-col${date === today ? ' nye-day-col--today' : ''}${onLeave || partial ? ' nye-day-col--leave' : ''}${
                    isPast ? ' nye-day-col--past' : ''
                  }`}
                >
                  <div
                    className={`nye-day-head${customizeMode ? ' nye-day-head--edit' : ''}`}
                    role={customizeMode ? 'button' : undefined}
                    tabIndex={customizeMode ? 0 : undefined}
                    onClick={customizeMode ? () => openCustomize(di) : undefined}
                    onKeyDown={customizeMode ? (e) => e.key === 'Enter' && openCustomize(di) : undefined}
                  >
                    {WEEK_DAYS[di]}
                    <br />
                    {date.slice(5)}
                    {customizeMode ? <div style={{ fontSize: 8, color: 'var(--nye-purple)' }}>Click to edit</div> : null}
                  </div>

                  {!isPast && free.length > 0 ? (
                    <>
                      <button type="button" className={`nye-day-btn${onLeave ? ' active' : ''}`} onClick={() => setDayOff(date, !onLeave)}>
                        {onLeave ? '✓ Full day leave' : 'Full day leave'}
                      </button>
                      {!onLeave ? (
                        <button type="button" className="nye-day-btn" onClick={() => setLeaveTimeDate(date)}>
                          Leave time…
                        </button>
                      ) : null}
                      {partial ? (
                        <div className="nye-slot nye-slot--blocked" style={{ marginBottom: 6 }}>
                          <span>🏖 {blockedCount} off</span>
                          <span className="nye-slot-actions">
                            <button type="button" title="Clear time off for this day" onClick={() => setDayOff(date, false)}>
                              ✕
                            </button>
                          </span>
                        </div>
                      ) : null}
                    </>
                  ) : null}

                  {daySlots.length === 0 ? (
                    <div style={{ fontSize: 10, color: 'var(--nye-soft)', textAlign: 'center' }}>—</div>
                  ) : (
                    daySlots.map((slot) => {
                      const slotPast = `${slot.slot_date}T${slot.start_time}` < `${today}T${new Date().toTimeString().slice(0, 5)}`;
                      const cls = slot.booked ? 'booked' : slot.blocked ? 'blocked' : 'free';
                      return (
                        <div key={slot.id} className={`nye-slot nye-slot--${cls}`} title={slot.customer_name ? `Booked: ${slot.customer_name}` : undefined}>
                          <span style={{ minWidth: 0 }}>
                            {slot.start_time}–{slot.end_time}
                            {slot.booked ? <small className="nye-ellipsis">{slot.customer_name || 'Booked'}</small> : null}
                            {slot.blocked ? <small>on leave</small> : null}
                          </span>
                          {!slotPast && !slot.booked ? (
                            <span className="nye-slot-actions">
                              {slot.blocked ? (
                                <button type="button" title="Remove leave for this slot" onClick={() => slotAction(slot, 'unblock')}>
                                  ✓
                                </button>
                              ) : (
                                <button type="button" title="Mark this slot off" onClick={() => slotAction(slot, 'block')}>
                                  🏖
                                </button>
                              )}
                              <button type="button" title="Remove this slot" onClick={() => slotAction(slot, 'remove')}>
                                🗑
                              </button>
                            </span>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </BusyOverlay>

      <div className="nye-legend">
        <span>
          <i style={{ background: 'rgba(29, 124, 119,0.06)', borderColor: 'rgba(29, 124, 119,0.3)' }} /> Free
        </span>
        <span>
          <i style={{ background: 'rgba(198,40,40,0.08)', borderColor: 'rgba(198,40,40,0.3)' }} /> Booked
        </span>
        <span>
          <i style={{ background: 'rgba(219, 45, 104,0.14)', borderColor: 'rgba(219, 45, 104,0.55)' }} /> On leave
        </span>
        <span>🏖 mark off · ✓ remove leave · 🗑 remove slot</span>
      </div>

      {customizeDay ? (
        <CustomizeDayModal
          day={customizeDay}
          ownerName={owner.name}
          onApply={applyCustomize}
          onRemoveAll={removeCustomize}
          onClose={() => setCustomizeDay(null)}
        />
      ) : null}
      {leaveTimeDate ? (
        <LeaveTimeModal ownerName={owner.name} date={leaveTimeDate} onSave={saveLeaveTime} onClose={() => setLeaveTimeDate(null)} />
      ) : null}
    </div>
  );
}
